import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import { snapToAngle45 } from '@/components/app/editor/tools/shared/helpers/snap-to-angle-45'
import type { NodeDragState } from '../types/node-edit-targets'
import { deepClonePoints } from './anchor-points'

interface DragModifiers {
  /** Shift held (event or keyboard-tracked): constrain anchor moves to 45°. */
  shift: boolean
  /** Alt held (event or keyboard-tracked): break a smooth point's handle symmetry. */
  alt: boolean
}

/** Moves each anchor in `indices` (and its handles) to its original position plus the delta. */
function translateAnchors(
  updated: PenAnchorPoint[],
  originalPoints: PenAnchorPoint[],
  indices: Iterable<number>,
  effectiveDx: number,
  effectiveDy: number
) {
  for (const idx of indices) {
    const selOrig = originalPoints[idx]
    const selPt = updated[idx]

    selPt.x = selOrig.x + effectiveDx
    selPt.y = selOrig.y + effectiveDy
    if (selPt.handleIn && selOrig.handleIn) {
      selPt.handleIn = {
        x: selOrig.handleIn.x + effectiveDx,
        y: selOrig.handleIn.y + effectiveDy,
      }
    }

    if (selPt.handleOut && selOrig.handleOut) {
      selPt.handleOut = {
        x: selOrig.handleOut.x + effectiveDx,
        y: selOrig.handleOut.y + effectiveDy,
      }
    }
  }
}

/**
 * The drag's points after moving its target by local-space delta (`dx`, `dy`) from where the drag
 * started. `createdHandles` reports an Alt-drag on a handle-less anchor pulling out a fresh pair of
 * symmetric handles (the point becomes smooth on release).
 */
export function computeDraggedPoints(
  drag: NodeDragState,
  dx: number,
  dy: number,
  modifiers: DragModifiers
): { points: PenAnchorPoint[]; createdHandles: boolean } {
  const updated = deepClonePoints(drag.originalPoints)
  const { target } = drag
  const pt = updated[target.index]
  const orig = drag.originalPoints[target.index]
  let createdHandles = false

  if (target.type === 'anchor') {
    if (drag.altKey && !orig.handleIn && !orig.handleOut) {
      pt.handleOut = { x: orig.x + dx, y: orig.y + dy }
      pt.handleIn = { x: orig.x - dx, y: orig.y - dy }
      createdHandles = true
    } else {
      // Shift: constrain movement to the nearest 45° axis from the drag origin
      let effectiveDx = dx
      let effectiveDy = dy

      if (modifiers.shift) {
        const snapped = snapToAngle45(orig.x, orig.y, orig.x + dx, orig.y + dy)

        effectiveDx = snapped.x - orig.x
        effectiveDy = snapped.y - orig.y
      }

      // Multi-drag: when the dragged anchor is part of a multi-selection, apply
      // the same delta to every selected anchor simultaneously.
      const isMultiDrag = drag.selectedIndices.size > 1 && drag.selectedIndices.has(target.index)

      translateAnchors(
        updated,
        drag.originalPoints,
        isMultiDrag ? drag.selectedIndices : [target.index],
        effectiveDx,
        effectiveDy
      )
    }
  } else {
    // Handle drag: move the dragged handle; a smooth point mirrors it onto the opposite handle,
    // unless Alt breaks the pair (the point becomes a corner).
    const side = target.type
    const opposite = side === 'handleOut' ? 'handleIn' : 'handleOut'

    pt[side] = { x: orig[side]!.x + dx, y: orig[side]!.y + dy }
    const moved = pt[side]

    if (pt.type === 'smooth' && moved) {
      if (modifiers.alt) {
        pt.type = 'corner'
      } else {
        pt[opposite] = { x: pt.x * 2 - moved.x, y: pt.y * 2 - moved.y }
      }
    }
  }

  updated[target.index] = pt

  return { points: updated, createdHandles }
}
